/**
 * Wire format + IPC contract for "export / import saved connections" (F01-P6).
 *
 * TWO HALVES, deliberately separable:
 *
 *  1. **The baseline** (`connections`) — endpoints only. Server, port, database,
 *     username, engine, SSH bastion coordinates. NO passwords, NO SSH
 *     passphrases, no `safeStorage` ciphertext. This half is plain JSON on
 *     purpose: it is the half the standard `ConnectionImport` framework reads
 *     back (`electron/connection-import/sources/jam.ts`), and the half a user
 *     can open in an editor before mailing it to a colleague.
 *
 *  2. **The optional secrets envelope** (`secrets`) — every saved password /
 *     SSH secret for the exported connections, as ONE AES-256-GCM blob whose
 *     key is derived from a user-chosen passphrase with scrypt. It is produced
 *     in the main process from `safeStorage`-decrypted values and is never
 *     handed to the renderer in any form. See `secretsEnvelope.ts`.
 *
 * Machine-bound `safeStorage` ciphertext is useless on another machine, which
 * is why re-wrapping under a passphrase is the only portable form. The
 * plaintext of half 2 NEVER reaches a file, a log, an analytics property or the
 * renderer — see `exportDocument.ts` and the byte-level test next to it.
 */

import { describeOracleDescriptor, redactTargetSecrets } from './targetSecrets';

// Describing a target without its secrets (C2 review C2-03) lives in `targetSecrets.ts`; re-exported for its callers.
export { describeOracleDescriptor, redactTargetSecrets };

/** Engines a transferred connection can carry (mirrors `ImportEngine`). */
export type TransferEngine = 'mssql' | 'pgsql' | 'mysql' | 'oracle' | 'sqlite' | 'kusto';

export const CONNECTION_EXPORT_KIND = 'jam-sql-studio-connections';
/** Bumped only on a breaking shape change; a reader refuses anything higher. */
export const CONNECTION_EXPORT_FORMAT_VERSION = 1;

/** SSH bastion coordinates, stripped of every secret-bearing field. */
export interface ExportedSshTunnel {
  enabled: boolean;
  host: string;
  port: number;
  user: string;
  /** `hasStoredPassphrase` / `hasStoredPassword` are dropped — they are machine facts. */
  auth: { kind: 'agent' } | { kind: 'key'; keyPath: string } | { kind: 'password' };
  sshConfigAlias?: string;
}

/** One connection in the baseline half. Every field here is non-secret. */
export interface ExportedConnection {
  /**
   * The SOURCE machine's connection id. Used for exactly one thing: keying the
   * secrets envelope. It is NEVER adopted as the imported connection's id (that
   * is freshly generated) and NEVER used as a path segment — see
   * `importConnections.ts`, which is where B12's `connectionId`-as-path-segment
   * lesson is enforced for this feature.
   */
  exportId: string;
  engine: TransferEngine;
  alias?: string;
  server: string;
  port: string;
  authentication: string;
  login?: string;
  database?: string;
  /** SQLite only: the database file on the SOURCE machine. */
  filePath?: string;
  ssl?: boolean;
  encrypt?: boolean;
  trustServerCertificate?: boolean;
  readOnly?: boolean;
  limitToDatabase?: boolean;
  color?: string;
  entraTenantId?: string;
  mcpPermissionLevel?: 'inherit' | 'block' | 'read-only' | 'confirm';
  oracleServiceName?: string;
  oracleUseSID?: boolean;
  oracleInitialSchema?: string;
  oracleConnectionStringType?: 'ezconnect' | 'tns_descriptor' | 'tns_alias';
  oracleTnsDescriptor?: string;
  oracleTnsAlias?: string;
  oracleTnsAdmin?: string;
  oracleWalletLocation?: string;
  /**
   * Browse Azure provenance (C3.1): the ARM id a pick or the user recorded, and whose it is. An imported id is a saved
   * id — the storage data-access layer refuses one naming another account (`resource-id-mismatch`) whatever its origin.
   */
  azureResourceId?: string;
  azureResourceIdOrigin?: 'picker' | 'user';
  sshTunnel?: ExportedSshTunnel;
  /** True when the source had a saved password. NOT the password. */
  hadPassword?: boolean;
}

/**
 * Every `ExportedConnection` field that `targetLine` seals. Exported so a test
 * can assert this list plus `COSMETIC_FIELDS` accounts for EVERY field the
 * exporter can emit.
 *
 * Review NEW-3 is why this exists: `oracleUseSID` — a target-resolution field
 * and the direct sibling of the already-sealed `oracleServiceName` — was in
 * neither list, inside the very diff that fixed the same class of omission.
 * A prose instruction to "classify new fields" was not enough; this is the
 * test that makes forgetting go red.
 */
export const DIGESTED_FIELDS = [
  'exportId',
  'engine',
  'server',
  'port',
  'database',
  'login',
  'filePath',
  'encrypt',
  'ssl',
  'trustServerCertificate',
  'authentication',
  'readOnly',
  'entraTenantId',
  'mcpPermissionLevel',
  'oracleServiceName',
  'oracleUseSID',
  'oracleConnectionStringType',
  'oracleTnsDescriptor',
  'oracleTnsAlias',
  'oracleTnsAdmin',
  'oracleWalletLocation',
  // The ARM id decides which account's keys are read (or refused): part of the target.
  'azureResourceId',
  'sshTunnel',
] as const;

/**
 * Fields deliberately NOT sealed: they cannot change where a credential goes,
 * how it travels, or what it may do. Leaving them out is what lets a user
 * rename a connection or reformat the file without destroying their secrets.
 */
export const COSMETIC_FIELDS = [
  'alias',
  'color',
  'oracleInitialSchema',
  'limitToDatabase',
  'hadPassword',
  // Whose the id is decides only whether the dialog clears it automatically — never where a credential goes.
  'azureResourceIdOrigin',
] as const;

/** Sealed sub-fields of `sshTunnel`. Nested keys were a blind spot: the
 * exhaustiveness test read top-level keys only, so `sshTunnel` counted as one
 * entry and a new field inside the tunnel — the highest-value target in the
 * record — would have been neither sealed nor caught. */
export const DIGESTED_SSH_FIELDS = [
  'enabled',
  'host',
  'port',
  'user',
  'sshConfigAlias',
  'auth',
] as const;

/** Sealed sub-fields of `sshTunnel.auth`. */
export const DIGESTED_SSH_AUTH_FIELDS = ['kind', 'keyPath'] as const;

/** KDF + cipher parameters, written into the file so a reader can derive the key. */
export interface ConnectionSecretsEnvelope {
  kdf: {
    algorithm: 'scrypt';
    /** Cost parameter. A reader REFUSES anything below `MIN_SCRYPT_N`. */
    N: number;
    r: number;
    p: number;
    keyLength: number;
    saltB64: string;
  };
  cipher: { algorithm: 'aes-256-gcm'; ivB64: string; authTagB64: string };
  payloadB64: string;
}

export interface ConnectionExportDocument {
  kind: typeof CONNECTION_EXPORT_KIND;
  formatVersion: number;
  /** Date only (no clock time) — a timestamp is a fingerprint nobody needs. */
  exportedOn: string;
  connections: ExportedConnection[];
  secrets?: ConnectionSecretsEnvelope;
}

/**
 * Minimum passphrase length for a NEW export. Enforced in the main process,
 * not only in the form — the renderer is not the authority on this. Not
 * enforced when OPENING a file: that passphrase was chosen by whichever build
 * wrote it, and refusing to open someone's own backup is not a security win.
 */
export const MIN_TRANSFER_PASSPHRASE_LENGTH = 12;

/* ------------------------------------------------------------------ IPC ---- */

export interface ConnectionExportRequest {
  /** Source-machine connection ids to export. Empty = nothing to write. */
  connectionIds: string[];
  /** When set, saved secrets for those ids are sealed under this passphrase. */
  passphrase?: string;
  suggestedFileName?: string;
  /** Test seam: skip the native save dialog. */
  filePath?: string;
}

export type ConnectionExportResult =
  | {
      success: true; filePath: string; exported: number; withSecrets: boolean;
      /** Connections whose embedded target secret could not travel so an import restores it exactly (shown as `***`). */
      targetsLeftStripped: number;
    }
  | { success: false; cancelled?: boolean; error?: string };

export interface ConnectionTransferPreviewRequest {
  /** Test seam: skip the native open dialog. */
  filePath?: string;
}

/** One row the user sees BEFORE anything is written. Rejected rows are reported. */
export interface ConnectionPreviewRow {
  exportId: string;
  engine: TransferEngine;
  alias: string;
  /** The connection target, exactly as it will be saved. */
  target: string;
  database?: string;
  login?: string;
  /** "user@host:port" when the record routes through an SSH bastion. */
  sshTarget?: string;
  /** The authentication mode, shown because the file chooses it. */
  authentication: string;
  /** Human transport summary; `undefined` where TLS is not a concept (SQLite). */
  transport?: string;
  /** True when the record asks for something weaker than verified TLS. */
  transportWeak?: boolean;
  hadPassword: boolean;
  duplicate: boolean;
}

/**
 * How a restored connection will protect its credential on the wire.
 *
 * Added for review C1. Widening the AAD stops a stolen export being *edited*
 * to disable TLS, but a field that is authenticated and invisible only proves
 * the file was not changed — not that the user agreed to what it says. A
 * connection that arrives with `trustServerCertificate: true` was never
 * tampered with; it is simply dangerous, and the review row has to say so.
 *
 * Shared across the process boundary because the export and import panels must
 * describe the same connection the same way — a discrepancy between "what you
 * ticked to send" and "what you ticked to receive" is its own trap.
 */
/**
 * Where a connection actually points, as one line.
 *
 * Shared for the same reason as `describeTransport`, and review NEW-5 is the
 * proof it was needed: the export panel showed `server:port` while the import
 * panel showed the Oracle TNS alias, contradicting `ConnectionPickList`'s own
 * promise that "what you tick to send is displayed exactly like what you tick
 * to receive". Two descriptions of one connection is how a user learns not to
 * read either.
 *
 * The Oracle branches are not cosmetic: with `tns_alias` the address is NOT
 * `server:port`, it is an alias resolved from a `tnsnames.ora` inside
 * `oracleTnsAdmin`, so showing `server:port` would display one destination
 * while the driver dialled another.
 */
export function describeTarget(c: {
  engine: string;
  server?: string;
  port?: string | number;
  filePath?: string;
  oracleConnectionStringType?: string;
  oracleTnsAlias?: string;
  oracleTnsAdmin?: string;
  oracleTnsDescriptor?: string;
}): string {
  const server = String(c.server ?? '');
  const port = String(c.port ?? '').trim();
  if (c.engine === 'sqlite') return c.filePath ?? server;
  if (c.engine === 'oracle') {
    if (c.oracleConnectionStringType === 'tns_alias' && c.oracleTnsAlias) {
      const from = c.oracleTnsAdmin ? ` (resolved from ${c.oracleTnsAdmin})` : '';
      return `TNS alias ${c.oracleTnsAlias}${from}`;
    }
    // Never the raw descriptor (Option C C2 review C2-03): it can carry credentials — `(SECURITY=(MY_WALLET_PASSWORD=…))`.
    if (c.oracleConnectionStringType === 'tns_descriptor' && c.oracleTnsDescriptor) {
      return describedDescriptor(c.oracleTnsDescriptor);
    }
    // A legacy record keeps the descriptor in its server field.
    if (server.trimStart().startsWith('(')) return describedDescriptor(server);
  }
  // Kusto exports port '0' (the cluster URL is the endpoint) — showing
  // `https://adx…:0` would be noise that reads like a real port; `<auto>` is
  // "let the driver pick", not a port.
  return redactTargetSecrets(port && port !== '0' && port !== '<auto>' ? `${server}:${port}` : server);
}

function describedDescriptor(descriptor: string): string {
  const described = describeOracleDescriptor(descriptor);
  return described === 'TNS descriptor' ? described : `${described} (TNS descriptor)`;
}

export function describeTransport(c: {
  engine: string;
  encrypt?: boolean;
  ssl?: boolean;
  trustServerCertificate?: boolean;
  // Oracle's transport depends on HOW the address is resolved, not only on the
  // wallet — see describeOracleTransport.
  oracleConnectionStringType?: string;
  oracleTnsDescriptor?: string;
  /** Not read by the label (the SID path is TCP either way) — accepted so a
   *  caller can pass the whole connection without narrowing it first. */
  oracleUseSID?: boolean;
  oracleWalletLocation?: string;
}): { label: string; weak: boolean } | undefined {
  switch (c.engine) {
    case 'sqlite':
      return undefined; // A local file — there is no wire.
    case 'kusto':
      return { label: 'HTTPS', weak: false };
    case 'mssql':
      // `mssqlDriverConnectionManager`: encrypt defaults true, trust defaults false.
      if (c.encrypt === false) return { label: 'TLS off', weak: true };
      if (c.trustServerCertificate === true) {
        return { label: 'TLS, certificate not verified', weak: true };
      }
      return { label: 'TLS', weak: false };
    case 'pgsql':
      // `pgsqlConnectionManager` hard-codes `rejectUnauthorized: false` ("Allow
      // self-signed certificates") whenever ssl is on. There is currently NO
      // configuration in which a PostgreSQL connection verifies the server
      // certificate, so returning a plain "TLS" here would be exactly the kind
      // of over-claim review I4 was about — one paragraph away from it.
      return c.ssl === true
        ? { label: 'TLS, certificate not verified', weak: true }
        : { label: 'TLS not requested', weak: true };
    case 'mysql':
      // `mysqlConnectionManager`: `rejectUnauthorized: !trustServerCertificate`,
      // so MySQL — unlike PostgreSQL — really can verify.
      if (c.ssl !== true) return { label: 'TLS not requested', weak: true };
      return c.trustServerCertificate === true
        ? { label: 'TLS, certificate not verified', weak: true }
        : { label: 'TLS', weak: false };
    case 'oracle':
      return describeOracleTransport(c);
    default:
      return undefined;
  }
}

/**
 * Oracle, based on what `oracleConnectionManager.buildConnectString` ACTUALLY
 * emits — not on what a wallet implies.
 *
 * Round-2 shipped `{ label: 'Wallet (mTLS)', weak: false }` keyed off the mere
 * presence of `oracleWalletLocation`, with a comment asserting it was genuine
 * mTLS and a test pinning that as correct. It was wrong, and it was wrong in a
 * file whose header promises each label is pinned to the manager:
 *
 *  - `ezconnect` (the default) emits `host:port/service` — no protocol prefix,
 *    i.e. **plain TCP**.
 *  - `oracleUseSID` emits `(ADDRESS=(PROTOCOL=TCP)…)` — hard-coded cleartext.
 *  - A wallet only affects transport when the descriptor asks for **TCPS**,
 *    which those two paths never do.
 *
 * So a wallet plus `oracleUseSID` was labelled secure while provably dialling
 * cleartext. Only a hand-written TNS descriptor can select TCPS here, and a
 * TNS alias resolves out of a `tnsnames.ora` this app never reads — genuinely
 * unknown, which is reported as unknown AND flagged, because "no claim" does
 * not satisfy a positive promise that weak transports are called out.
 */
function describeOracleTransport(c: {
  oracleConnectionStringType?: string;
  oracleTnsDescriptor?: string;
  oracleWalletLocation?: string;
}): { label: string; weak: boolean } {
  const kind = c.oracleConnectionStringType ?? 'ezconnect';

  if (kind === 'tns_alias') {
    return { label: 'Transport unknown — resolved from tnsnames.ora', weak: true };
  }

  if (kind === 'tns_descriptor') {
    const descriptor = c.oracleTnsDescriptor ?? '';
    const hasTcps = /PROTOCOL\s*=\s*TCPS/i.test(descriptor);
    // `(?![A-Za-z])` so TCPS does not read as a plain-TCP address. A descriptor
    // may list SEVERAL addresses; if any one of them is cleartext, Oracle may
    // dial that one, so the presence of a TCPS address is not enough.
    const hasPlainTcp = /PROTOCOL\s*=\s*TCP(?![A-Za-z])/i.test(descriptor);

    if (!hasTcps) return { label: 'Not encrypted (TCP)', weak: true };
    if (hasPlainTcp) {
      return { label: 'Mixed TCPS and plain TCP addresses', weak: true };
    }
    return c.oracleWalletLocation
      ? { label: 'TLS (TCPS) with wallet', weak: false }
      : { label: 'TLS (TCPS), no wallet configured', weak: true };
  }

  // ezconnect, and the SID variant of it, both dial plain TCP in this codebase
  // whether or not a wallet is configured.
  return { label: 'Not encrypted (TCP)', weak: true };
}

export type ConnectionTransferPreviewResult =
  | {
      success: true;
      filePath: string;
      rows: ConnectionPreviewRow[];
      /** Records rejected by validation, with a human reason. Never silent. */
      skipped: { index: number; reason: string }[];
      hasSecrets: boolean;
    }
  | { success: false; cancelled?: boolean; error?: string };

export interface ConnectionTransferApplyRequest {
  filePath: string;
  /** Export ids the user ticked. Anything not listed is not imported. */
  exportIds: string[];
  /** Only sent when the user asked to restore passwords. Never persisted. */
  passphrase?: string;
}

export type ConnectionTransferApplyResult =
  | {
      success: true; imported: number;
      /** Saved passwords, SSH secrets and sealed target texts put back. */
      secretsRestored: number;
      /** Safe names of the connections whose sealed target text could not be put back — they ask for it again. */
      targetsNotRestored: string[];
    }
  | { success: false; error: string; badPassphrase?: boolean };
